// E2E pipeline-lineage: pure helpers for the Data Pipeline TRACE mode. Maps the
// per-stage rows of one item onto the flow-diagram nodes and reduces them to a
// paint overlay + a normalized status for the timeline pills.

import type { DpNodeId } from '@/lib/flow-layout';
import type { Band } from '@/lib/system-metrics';
import type {
  LineageDestination,
  LineageStage,
  LineageStatus,
  PipelineLineageRow,
  Uid2State,
} from '@/api/types.gen';

export type LineageKind = 'web' | 'ticket';

/** How far a node is along the item's journey (drives node paint). */
export type TraceStatus =
  | 'delivered'
  | 'retry'
  | 'failed'
  | 'traversed'
  | 'pending'
  | 'unreached';

export interface TraceNode {
  status: TraceStatus;
  /** The row that set this node's status (null for an implied `traversed`/`pending`). */
  row: PipelineLineageRow | null;
}

export interface TraceOverlay {
  kind: LineageKind;
  nodes: Partial<Record<DpNodeId, TraceNode>>;
  /** Furthest spine index reached (−1 = nothing yet). */
  furthestIndex: number;
}

/** Fixed stage → diagram node. `ingest` resolves per-kind (see `nodeForStage`). */
export const STAGE_TO_NODE: Record<LineageStage, DpNodeId | null> = {
  ingest: null,
  enrich: 'enrich',
  enqueue: 'conversion-queue',
  meta_build: 'meta',
  meta_send: 'meta',
  uid2_map: 'ttd',
  ttd_build: 'ttd',
  ttd_send: 'ttd',
};

// The linear "spine" per lane (source node handled separately). Nodes we don't
// emit lineage for (pixel-edge/batch-ingest, ingest-jobs) are included so they
// light as `traversed` once a downstream node is reached.
const WEB_SPINE: DpNodeId[] = [
  'pixel-edge',
  'batch-ingest',
  'store-web-events',
  'enrich',
  'conversion-queue',
];
const TICKET_SPINE: DpNodeId[] = ['ingest-jobs', 'store-tickets', 'enrich', 'conversion-queue'];

const RANK: Record<TraceStatus, number> = {
  unreached: 0,
  traversed: 1,
  pending: 2,
  delivered: 3,
  retry: 4,
  failed: 5,
};

function statusFromLineage(s: LineageStatus): TraceStatus {
  switch (s) {
    case 'ok':
      return 'delivered';
    case 'retrying':
      return 'retry';
    case 'failed':
    case 'quarantined':
      return 'failed';
    case 'skipped':
      return 'traversed';
    case 'pending':
      return 'pending';
    default:
      return 'traversed';
  }
}

/** The diagram node a stage row paints onto (`ingest` resolves per-kind). */
export function stageNode(stage: LineageStage, kind: LineageKind): DpNodeId | null {
  if (stage === 'ingest') return kind === 'ticket' ? 'store-tickets' : 'store-web-events';
  return STAGE_TO_NODE[stage] ?? null;
}

/** The band a stage's status maps to for the timeline/panel pill. */
export function statusBand(s: LineageStatus): Band {
  switch (s) {
    case 'ok':
      return 'ok';
    case 'retrying':
      return 'warn';
    case 'failed':
    case 'quarantined':
      return 'critical';
    case 'pending':
    case 'skipped':
    default:
      return 'unknown';
  }
}

/** `web:`/`tkt:` → lane; anything else defaults to the web lane. */
export function kindFromCorrelationId(cid: string): LineageKind {
  return cid.startsWith('tkt:') ? 'ticket' : 'web';
}

/**
 * Coerce user input into a canonical correlation_id. Accepts a `web:`/`tkt:`/
 * `unk:` id verbatim; a bare UUID → `web:` (the pixel-debug common case); any
 * other bounded slug → `tkt:`. Returns `''` when it can't form a valid id (the
 * caller shows an inline hint and does not fetch).
 */
export function normalizeCorrelationId(input: string): string {
  const s = input.trim();
  if (!s) return '';
  if (/^(web|tkt|unk):.+$/.test(s)) return s.length <= 64 ? s : '';
  if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F-]+$/.test(s)) {
    const id = `web:${s}`;
    return id.length <= 64 ? id : '';
  }
  if (/^[A-Za-z0-9_-]+$/.test(s)) {
    const id = `tkt:${s}`;
    return id.length <= 64 ? id : '';
  }
  return '';
}

/**
 * Is `input` a bare (unprefixed) UUID? EncorePro `ticket_id`s AND web-event ids
 * are BOTH UUIDs, so a bare UUID is genuinely ambiguous and cannot be routed to
 * a lane by shape alone — the caller must probe both `tkt:` and `web:`. (Same
 * shape test `normalizeCorrelationId` uses for its legacy web-default branch.)
 */
export function isBareUuid(input: string): boolean {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F-]+$/.test(input.trim());
}

/**
 * Every row field the TRACE UI renders (stage panel k/v grid + timeline pill),
 * as one comparable signature. `ts` and `lineage_id` are deliberately EXCLUDED:
 * the nightly re-scan re-stamps both on otherwise-identical re-emits, so they
 * differ on every true duplicate.
 */
function payloadKey(r: PipelineLineageRow): string {
  return JSON.stringify([
    r.status, r.outcome, r.target, r.attempt, r.http_status, r.fbtrace_id,
    r.value, r.currency, r.units, r.order_id, r.event_id, r.production_id,
    r.venue_id, r.pixel_id, r.channel, r.event_type, r.signals,
    r.uid2_token_count, r.has_email_hash, r.has_phone_hash, r.has_name_hash,
    r.person_id_present, r.household_id_present, r.user_id_present,
    r.error_summary,
    // A tenant boundary is a step boundary: an observation recorded under
    // another tenant must keep its own step (and its marker).
    r.tenant_id,
  ]);
}

/** Queue statuses that end a destination's journey. */
const TERMINAL_DESTINATION_STATUSES: ReadonlySet<string> = new Set([
  'delivered', 'failed', 'quarantined', 'skipped', 'logged',
]);

/** Is one destination's queue status terminal? (`pending` / `sending` are not.) */
export function isTerminalDestination(status: string): boolean {
  return TERMINAL_DESTINATION_STATUSES.has(status);
}

/** The newest row of each (stage, target), so a superseded attempt never counts. */
function latestPerStage(rows: readonly PipelineLineageRow[]): PipelineLineageRow[] {
  const latest = new Map<string, PipelineLineageRow>();
  for (const r of rows) latest.set(r.stage + '|' + (r.target ?? ''), r); // rows arrive ts-ascending
  return [...latest.values()];
}

/**
 * How long a terminal queue status may wait for the lineage row recording that
 * attempt. Delivery commits the queue row first and hands the lineage row to an
 * asynchronous flusher, so a poll can see `delivered` while the timeline still
 * ends in `retrying`.
 */
export const LINEAGE_CATCH_UP_MS = 30_000;

/**
 * Where a trace stands:
 * - `loading`: nothing read yet, or no lineage rows; the poll keeps looking.
 * - `in_flight`: a destination is still sending, a stage is pending, or a
 *   terminal destination's own lineage row has not been flushed yet.
 * - `awaiting_enqueue`: lineage exists but the item is not in the queue, and
 *   nothing says it never will be: the ticket lane enqueues in bounded batches
 *   on a configurable cron, so no wait is long enough to call it final. The
 *   poll slows to once a minute instead of stopping.
 * - `settled`: finished, or historical.
 */
export type TraceState = 'loading' | 'in_flight' | 'awaiting_enqueue' | 'settled';

type TraceData = {
  as_of: string;
  correlation_id?: string;
  rows: readonly PipelineLineageRow[];
  destinations?: readonly LineageDestination[];
  ticket_lookup?: string | null;
};

const SEND_STAGE: Readonly<Record<string, string>> = { meta: 'meta_send', ttd: 'ttd_send' };

const isOpenStatus = (s: string) => s === 'pending' || s === 'retrying';

/**
 * THE one answer to "is this trace finished?", shared by the lineage poll
 * (`flowLineage`'s cadence), the diagram overlay (`dpTraceDerive`) and the
 * panel's "Still in flight" line, so the three can never disagree. Ages are
 * measured on the server's clock (`as_of`), never the browser's.
 *
 * - The queue lists destinations: settled only when EVERY one is terminal and
 *   the timeline has caught up: each has a closed latest send row written at
 *   or after its queue row last changed (delivery stamps the queue when the
 *   provider answers and writes the lineage row after it, so an older send
 *   row is an earlier attempt or an earlier, collected queue row), or
 *   `LINEAGE_CATCH_UP_MS` has passed since the newest queue change.
 * - The queue lists none, but the item has an `enqueue` row: it was queued
 *   and `conversions_gc` has removed the rows since, so it is historical. (An
 *   enqueue row is only ever `pending`; nothing writes a later one.)
 * - No queue row and no enqueue row: in flight while a stage is pending or
 *   retrying, otherwise awaiting the enqueue. An `unk:` id names no queue
 *   row, so it is settled.
 */
export function traceState(data: TraceData | undefined): TraceState {
  if (!data) return 'loading';
  const destinations = data.destinations ?? [];
  if (data.rows.length === 0) {
    // No observation retained. The queue still decides when it holds the item;
    // a stored ticket with no queue row is as settled as a trace can be (its
    // outcome is unknown, and "Ticket now" keeps refreshing); nothing at all
    // keeps looking, for an item not ingested yet.
    if (destinations.length > 0)
      return destinations.every((d) => isTerminalDestination(d.status)) ? 'settled' : 'in_flight';
    return data.ticket_lookup && data.ticket_lookup !== 'not_found' ? 'settled' : 'loading';
  }
  const asOf = Date.parse(data.as_of);
  const latest = latestPerStage(data.rows);
  if (destinations.length > 0) {
    if (!destinations.every((d) => isTerminalDestination(d.status))) return 'in_flight';
    const caughtUp = destinations.every((d) => {
      const send = latest.find((r) => r.stage === SEND_STAGE[d.target] && r.target === d.target);
      return !!send && !isOpenStatus(send.status) && Date.parse(send.ts) >= Date.parse(d.updated_at);
    });
    if (caughtUp) return 'settled';
    const changed = Math.max(...destinations.map((d) => Date.parse(d.updated_at)));
    return asOf - changed >= LINEAGE_CATCH_UP_MS ? 'settled' : 'in_flight';
  }
  // Queued once, or sent: the queue row has since been collected, so it is
  // historical. A recorded send proves the queueing even when its enqueue
  // observation is no longer retained.
  if (latest.some((r) => r.stage === 'enqueue' || ((r.stage === 'meta_send' || r.stage === 'ttd_send') && !isOpenStatus(r.status))))
    return 'settled';
  if (latest.some((r) => isOpenStatus(r.status))) return 'in_flight';
  const cid = data.correlation_id ?? data.rows[0]!.correlation_id;
  return cid.startsWith('unk:') ? 'settled' : 'awaiting_enqueue';
}

/** `traceState(data) === 'settled'`: the poll stops, nothing is drawn in flight. */
export function traceIsSettled(data: TraceData | undefined): boolean {
  return traceState(data) === 'settled';
}

const DESTINATION_NODES: ReadonlyArray<readonly [DpNodeId, string]> = [['meta', 'meta'], ['ttd', 'ttd']];

/** A destination's queue status → its node paint (`sending` keeps the attempt's own word). */
function destinationPaint(status: string, latestSend: PipelineLineageRow | null): TraceStatus {
  switch (status) {
    case 'delivered':
      return 'delivered';
    case 'failed':
    case 'quarantined':
      return 'failed';
    case 'skipped':
    case 'logged':
      return 'traversed';
    default:
      // pending / sending: in flight; a retry still says so.
      return latestSend?.status === 'retrying' ? 'retry' : 'pending';
  }
}

/**
 * Is a diagram edge on the traced item's own path? Both of its ends must have
 * been reached. Reaching the downstream node alone is not enough: the web
 * edge (Batch Ingest → Conversion Queue) and the ticket edge (Tickets store →
 * Conversion Queue) share their downstream node, and only the edge the item
 * actually took may light.
 */
export function traceEdgeActive(trace: TraceOverlay, from: DpNodeId, to: DpNodeId): boolean {
  const reached = (id: DpNodeId) => {
    const s = trace.nodes[id]?.status;
    return !!s && s !== 'unreached';
  };
  return reached(from) && reached(to);
}

/**
 * Reduce an item's per-stage rows to a diagram paint overlay.
 *
 * A destination (Meta, TTD) paints its LATEST attempt's outcome: a send that
 * failed and then succeeded is delivered, and the failure stays visible in the
 * timeline. With the queue's `destinations`, the queue's current status wins.
 */
export function dpTraceDerive(
  rows: PipelineLineageRow[],
  kind: LineageKind,
  destinations: readonly LineageDestination[] = [],
  asOf: string = new Date(0).toISOString(),
): TraceOverlay {
  const nodes: Partial<Record<DpNodeId, TraceNode>> = {};
  const put = (id: DpNodeId, status: TraceStatus, row: PipelineLineageRow | null) => {
    const cur = nodes[id];
    if (!cur || RANK[status] > RANK[cur.status]) nodes[id] = { status, row };
  };

  const isDestination = (id: DpNodeId) => id === 'meta' || id === 'ttd';
  for (const r of rows) {
    const node = stageNode(r.stage, kind);
    // An enqueue row records the queueing and is only ever written `pending`;
    // where delivery stands is the queue's own row (`destinations`), painted on
    // the destination nodes below. Painting the queue from it kept the node
    // amber for every item, delivered ones included.
    const status = r.stage === 'enqueue' ? 'traversed' : statusFromLineage(r.status);
    if (node && !isDestination(node)) put(node, status, r);
  }

  // Destinations: the latest send decides, else the latest of their other
  // stages (build / uid2 map); the queue's status overrides both.
  for (const [node, target] of DESTINATION_NODES) {
    const own = rows.filter((r) => stageNode(r.stage, kind) === node);
    const sends = own.filter((r) => r.stage === 'meta_send' || r.stage === 'ttd_send');
    const latestSend = sends.length ? sends[sends.length - 1]! : null;
    const latest = latestSend ?? (own.length ? own[own.length - 1]! : null);
    const dest = destinations.find((d) => d.target === target);
    if (dest) {
      nodes[node] = { status: destinationPaint(dest.status, latestSend), row: latest };
    } else if (latest) {
      nodes[node] = { status: statusFromLineage(latest.status), row: latest };
    }
  }

  // The queue node is amber while the queue still holds an unfinished row for
  // the item, and passed once every row it holds is terminal or collected.
  if (nodes['conversion-queue'] && destinations.some((d) => !isTerminalDestination(d.status))) {
    put('conversion-queue', 'pending', nodes['conversion-queue'].row);
  }

  // Source node: web lane picks by the ingest row's channel; ticket lane fixed.
  let sourceNode: DpNodeId;
  if (kind === 'ticket') {
    sourceNode = 'encore-pg';
  } else {
    const ing = rows.find((r) => r.stage === 'ingest');
    sourceNode = ing?.channel === 'pixel_sgtm' ? 'sgtm' : 'web-pixel';
  }

  // Traversed fill: everything on the spine up to the furthest reached node
  // (and the source) is implied to have been passed through.
  const spine = kind === 'ticket' ? TICKET_SPINE : WEB_SPINE;
  let furthest = -1;
  spine.forEach((id, i) => {
    if (nodes[id]) furthest = Math.max(furthest, i);
  });
  if (furthest >= 0) {
    for (let i = 0; i <= furthest; i++) {
      if (!nodes[spine[i]!]) put(spine[i]!, 'traversed', null);
    }
    if (!nodes[sourceNode]) put(sourceNode, 'traversed', null);
  }

  // Enqueued and not settled → a destination the queue lists is in flight.
  // Only listed destinations: an item queued for Meta alone never lights TTD.
  if (nodes['conversion-queue'] && traceState({ as_of: asOf, rows, destinations }) === 'in_flight') {
    for (const [node, target] of DESTINATION_NODES) {
      if (!nodes[node] && destinations.some((d) => d.target === target)) put(node, 'pending', null);
    }
  }

  return { kind, nodes, furthestIndex: furthest };
}

// ── The journey view (Data Pipeline trace) ─────────────────────────────────

/** Where the traced item ended up, as the header chip says it. */
export type TraceOutcome =
  | 'failed'
  | 'quarantined'
  | 'retrying'
  | 'in_flight'
  | 'partly_delivered'
  | 'delivered'
  | 'logged_only'
  | 'skipped'
  | 'no_queue_evidence';

export const OUTCOME_LABEL: Record<TraceOutcome, string> = {
  failed: 'Failed',
  quarantined: 'Quarantined',
  retrying: 'Retrying',
  in_flight: 'In flight',
  partly_delivered: 'Partly delivered',
  delivered: 'Delivered',
  logged_only: 'Logged only',
  skipped: 'Skipped',
  no_queue_evidence: 'No retained queue evidence',
};

/** One destination's queue row as an outcome. */
export function destinationOutcome(d: Pick<LineageDestination, 'status' | 'failure_kind'>): TraceOutcome {
  switch (d.status) {
    case 'failed':
      return 'failed';
    case 'quarantined':
      return 'quarantined';
    case 'delivered':
      return 'delivered';
    case 'logged':
      return 'logged_only';
    case 'skipped':
      return 'skipped';
    default:
      // pending / sending: a recorded error means an earlier attempt failed.
      return d.failure_kind === 'retrying' ? 'retrying' : 'in_flight';
  }
}

/**
 * The whole item's outcome over every destination, by fixed precedence:
 * Failed > Quarantined > Retrying > In flight > Partly delivered > Delivered >
 * Logged only > Skipped. Delivered beside anything that is not a delivery reads
 * as Partly delivered. No queue row at all is not a verdict on delivery.
 */
export function traceOutcome(
  destinations: readonly Pick<LineageDestination, 'status' | 'failure_kind'>[],
): TraceOutcome {
  if (destinations.length === 0) return 'no_queue_evidence';
  const outcomes = destinations.map(destinationOutcome);
  for (const o of ['failed', 'quarantined', 'retrying', 'in_flight'] as const) {
    if (outcomes.includes(o)) return o;
  }
  if (outcomes.includes('delivered')) {
    return outcomes.every((o) => o === 'delivered') ? 'delivered' : 'partly_delivered';
  }
  return outcomes.includes('logged_only') ? 'logged_only' : 'skipped';
}

/** The chip's band. */
export function outcomeBand(o: TraceOutcome): Band {
  switch (o) {
    case 'failed':
    case 'quarantined':
      return 'critical';
    case 'retrying':
    case 'partly_delivered':
      return 'warn';
    case 'delivered':
    case 'logged_only':
      return 'ok';
    default:
      return 'unknown';
  }
}

/** A queue reason in words. A skip is the queue's verdict, never proof of what happened at the source. */
export function reasonWords(reason: string | null | undefined): string | null {
  if (!reason) return null;
  switch (reason) {
    case 'stale':
      return 'too old to send';
    case 'no_destination':
      return 'no destination configured';
    case 'no_uid2':
      return 'no UID2 token';
    case 'refunded':
      return 'refunded / not deliverable';
    case 'max_attempts':
      return 'gave up after its attempts';
    case 'no_identity':
      return 'no contact details to match on';
    case 'meta_ack_short':
    case 'ttd_ack_lost':
      return 'acknowledgement incomplete';
    case 'bad_payload':
      return 'the conversion could not be built';
    default:
      return reason.replaceAll('_', ' ');
  }
}

/** The journey's phases, in order. */
export type JourneyPhase = 'source' | 'stored' | 'identity' | 'queued' | 'meta' | 'ttd';
export const JOURNEY_PHASES: readonly JourneyPhase[] = ['source', 'stored', 'identity', 'queued', 'meta', 'ttd'];

export function phaseLabel(phase: JourneyPhase, kind: LineageKind): string {
  switch (phase) {
    case 'source':
      return kind === 'ticket' ? 'Source · EncorePro' : 'Source · Pixel';
    case 'stored':
      return 'Stored in SAM';
    case 'identity':
      return 'Identity';
    case 'queued':
      return 'Queued';
    case 'meta':
      return 'Meta';
    case 'ttd':
      return 'The Trade Desk';
  }
}

/** The phase a stage's observations belong to. A web event's ingest is its source. */
export function stagePhase(stage: LineageStage, kind: LineageKind): JourneyPhase {
  switch (stage) {
    case 'ingest':
      return kind === 'ticket' ? 'stored' : 'source';
    case 'enrich':
      return 'identity';
    case 'enqueue':
      return 'queued';
    case 'meta_build':
    case 'meta_send':
      return 'meta';
    default:
      return 'ttd';
  }
}

/** A presence field that changed between two observations of one stage. */
export interface FieldChange {
  label: string;
  from: string;
  to: string;
}

/** Consecutive equivalent observations of one stage, as one step. */
export interface JourneyStep {
  /** The first observation's `lineage_id`: the step's identity for selection. */
  id: string;
  stage: LineageStage;
  phase: JourneyPhase;
  first: PipelineLineageRow;
  last: PipelineLineageRow;
  count: number;
  /** What differs from this stage's previous step; empty for the first. */
  changes: FieldChange[];
}

const yesNo = (v: boolean | null) => (v == null ? 'not recorded' : v ? 'yes' : 'no');

const CHANGE_FIELDS: ReadonlyArray<readonly [string, (r: PipelineLineageRow) => string]> = [
  ['status', (r) => r.status],
  ['outcome', (r) => r.outcome ?? 'none'],
  ['person resolved', (r) => yesNo(r.person_id_present)],
  ['household', (r) => yesNo(r.household_id_present)],
  ['buyer id', (r) => yesNo(r.user_id_present)],
  ['email hash', (r) => yesNo(r.has_email_hash)],
  ['phone hash', (r) => yesNo(r.has_phone_hash)],
  ['name hash', (r) => yesNo(r.has_name_hash)],
  ['units', (r) => (r.units == null ? 'not recorded' : String(r.units))],
  ['value', (r) => (r.value == null ? 'not recorded' : r.value.toFixed(2))],
  ['production', (r) => r.production_id ?? 'none'],
  ['UID2 tokens', (r) => (r.uid2_token_count == null ? 'not recorded' : String(r.uid2_token_count))],
];

/**
 * Fold each stage's consecutive equivalent observations into one step ("observed
 * 12 times"). Equivalence is every recorded field but the instant and the id
 * (`payloadKey`), per stage and target, so a recurrence after a different value
 * is a new step, never merged into the earlier one, and the change is named.
 * Steps are ordered by their first observation. Rows arrive oldest first.
 */
export function foldJourney(rows: readonly PipelineLineageRow[], kind: LineageKind): JourneyStep[] {
  const steps: JourneyStep[] = [];
  const open = new Map<string, JourneyStep>();
  for (const row of rows) {
    const lane = row.stage + '|' + (row.target ?? '');
    const current = open.get(lane);
    if (current && payloadKey(current.last) === payloadKey(row)) {
      current.last = row;
      current.count += 1;
      continue;
    }
    const changes: FieldChange[] = current
      ? CHANGE_FIELDS.flatMap(([label, read]) => {
          const from = read(current.last);
          const to = read(row);
          return from === to ? [] : [{ label, from, to }];
        })
      : [];
    const step: JourneyStep = {
      id: row.lineage_id,
      stage: row.stage,
      phase: stagePhase(row.stage, kind),
      first: row,
      last: row,
      count: 1,
      changes,
    };
    steps.push(step);
    open.set(lane, step);
  }
  return steps;
}

const money = (value: number | null, currency: string | null) =>
  value == null ? null : `${currency === 'USD' || !currency ? '$' : currency + ' '}${value.toFixed(2)}`;

const presentList = (r: PipelineLineageRow) =>
  [
    r.has_email_hash ? 'email' : null,
    r.has_phone_hash ? 'phone' : null,
    r.has_name_hash ? 'name' : null,
  ].filter((x): x is string => x != null);

const targetName = (target: string | null) =>
  target === 'meta' ? 'Meta' : target === 'ttd' ? 'The Trade Desk' : 'the destination';

/**
 * What one observation recorded, as a sentence built from its own fields only.
 * Nothing is inferred: a field the row does not carry is not mentioned, and an
 * HTTP status appears only when one was recorded.
 */
export function stepSentence(row: PipelineLineageRow, kind: LineageKind): string {
  const http = row.http_status != null ? ` (HTTP ${row.http_status})` : '';
  const where = targetName(row.target);
  switch (row.stage) {
    case 'ingest': {
      const what = kind === 'ticket' ? 'SAM stored the ticket from EncorePro' : 'SAM received the event from the pixel';
      const amount = money(row.value, row.currency);
      const parts = [
        row.units != null ? `${row.units} unit${row.units === 1 ? '' : 's'}` : null,
        amount,
      ].filter(Boolean);
      const present = presentList(row);
      return [
        what + (parts.length ? `: ${parts.join(', ')}` : '') + '.',
        present.length ? `Hashed ${present.join(', ')} present.` : null,
        row.person_id_present != null ? (row.person_id_present ? 'A person was resolved.' : 'No person was resolved yet.') : null,
      ]
        .filter(Boolean)
        .join(' ');
    }
    case 'enrich':
      switch (row.status) {
        case 'ok':
          // A web lookup that failed is recorded the same way as a miss, so a
          // miss is only what was recorded, never "checked and none found".
          return row.person_id_present
            ? `Matched to a person${row.household_id_present ? ' and a household' : ''}.`
            : 'No person match was recorded.';
        case 'skipped':
          return `Person matching was skipped: ${reasonWords(row.outcome) ?? 'no reason recorded'}.`;
        default:
          return `Person matching did not complete${row.outcome ? `: ${reasonWords(row.outcome)}` : ''}.`;
      }
    case 'enqueue':
      return 'Queued for delivery.';
    case 'uid2_map':
      switch (row.status) {
        case 'ok':
          return row.uid2_token_count != null
            ? `Mapped to ${row.uid2_token_count} UID2 token${row.uid2_token_count === 1 ? '' : 's'} for The Trade Desk.`
            : 'The UID2 step for The Trade Desk completed.';
        case 'retrying':
          return 'The UID2 mapping did not finish; SAM scheduled another attempt.';
        case 'skipped':
          // Recorded both when there was nothing to map and when the mapping ran
          // and returned no token, so it says only what both mean.
          return 'No UID2 token was available, so The Trade Desk delivery was skipped.';
        default:
          return 'The UID2 mapping did not finish, and SAM stopped trying.';
      }
    case 'meta_build':
    case 'ttd_build':
      return `SAM could not build the conversion for ${where}.`;
    default:
      // Each send row is one attempt's record, so it speaks in the past tense;
      // where delivery stands now is the queue's row (Deliveries).
      switch (row.status) {
        case 'ok':
          return row.outcome === 'logged'
            ? `Logged for ${where} without sending (log-only mode).`
            : `${where} accepted it${http}.`;
        case 'retrying':
          // Not confirming is all a retry proves: a lost or short acknowledgement
          // retries too, and the platform may have kept that attempt.
          if (row.outcome === 'ack_short_retry')
            return `${where}'s acknowledgement was incomplete, so whether it kept the conversion is unknown; SAM scheduled another attempt.`;
          return `${where} did not confirm it${http}; SAM scheduled another attempt.`;
        case 'failed':
          return `${where} never confirmed it${http}, and SAM stopped trying after its attempts.`;
        case 'quarantined':
          // Quarantine is SAM's decision. Only a recorded 4xx is the platform's
          // refusal; a lost or short acknowledgement means SAM cannot know
          // whether the platform kept it.
          if (row.outcome === 'meta_ack_short' || row.outcome === 'ttd_ack_lost')
            return `${where}'s acknowledgement was incomplete, so whether it kept the conversion is unknown; SAM will not send it again.`;
          if (row.http_status != null && row.http_status >= 400 && row.http_status < 500)
            return `${where} refused it${http}; SAM will not send it again.`;
          return `SAM set it aside for ${where}${row.outcome ? ` (${reasonWords(row.outcome)})` : ''} and will not send it again.`;
        case 'skipped':
          return `Not sent to ${where}: ${reasonWords(row.outcome) ?? 'skipped'}.`;
        default:
          return `Waiting to send to ${where}.`;
      }
  }
}

/** A buyer's UID2 state in words, from the parsed counts only. */
export function uid2Words(state: Uid2State): string[] {
  switch (state.state) {
    case 'none':
      return ['No UID2 identities recorded.'];
    case 'unreadable':
      return ['The stored UID2 data could not be read.'];
    case 'parsed': {
      const out: string[] = [];
      out.push(state.tokens > 0 ? `UID2 tokens present: ${state.tokens}.` : 'No UID2 token.');
      if (state.declined > 0)
        out.push(`${state.declined} ${state.declined === 1 ? 'identity' : 'identities'} declined by UID2.`);
      if (state.oversized) {
        out.push('Account above the 128-identity cap; UID2 does not map it.');
        if (state.tokens > 0) out.push('Its remaining tokens are removed at the next refresh.');
        if (state.pending > 0) out.push(`${state.pending} ${state.pending === 1 ? 'identity' : 'identities'} not mapped (over the cap).`);
      } else if (state.pending > 0) {
        out.push(`${state.pending} ${state.pending === 1 ? 'identity' : 'identities'} waiting for a mapping.`);
      }
      return out;
    }
  }
}
